import { Close as CloseIcon, Send as SendIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { supportMessageLib } from 'entities/support-message';

import { ESupportMessageStatus, SupportMessage } from 'shared/api';
import { useFileUrl } from 'shared/lib';

type Props = {
  message: SupportMessage | null;
  isOpen: boolean;
  isLoading: boolean;
  isReplying: boolean;
  onClose: () => void;
  onReply: (replyText: string) => void;
};

const statusConfig: Record<ESupportMessageStatus, { label: string; color: 'error' | 'warning' | 'success' }> = {
  [ESupportMessageStatus.New]: { label: 'New', color: 'error' },
  [ESupportMessageStatus.Read]: { label: 'Read', color: 'warning' },
  [ESupportMessageStatus.Replied]: { label: 'Replied', color: 'success' },
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const PhotoImage = ({ path }: { path: string }) => {
  const src = useFileUrl(path);

  if (!src) return <Box sx={{ width: 150, height: 150, bgcolor: 'grey.200', borderRadius: 1 }} />;

  return (
    <Box
      component="img"
      src={src}
      alt="Attachment"
      sx={{
        width: 150,
        height: 150,
        objectFit: 'cover',
        borderRadius: 1,
        cursor: 'pointer',
        '&:hover': { opacity: 0.8 },
      }}
      onClick={() => window.open(src, '_blank')}
    />
  );
};

export const SupportMessageDialog = ({ message, isOpen, isLoading, isReplying, onClose, onReply }: Props) => {
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setReplyText('');
    }
  }, [isOpen]);

  const handleReply = () => {
    if (replyText.trim()) {
      onReply(replyText.trim());
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Support Message Details
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading || !message ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Header info */}
            <Box display="flex" flexDirection="column" gap={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status:
                </Typography>
                <Chip
                  label={statusConfig[message.status].label}
                  color={statusConfig[message.status].color}
                  size="small"
                />
              </Box>
              <Box display="flex" gap={4}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Subject
                  </Typography>
                  <Typography>{supportMessageLib.getSupportSubjectText(message.subject)}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography>{message.email}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Date
                  </Typography>
                  <Typography>{formatDate(message.createdAt)}</Typography>
                </Box>
              </Box>
            </Box>

            <Divider />

            {/* Message text */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Message
              </Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{message.text}</Typography>
            </Box>

            {/* Photos */}
            {message.photos && message.photos.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Attachments ({message.photos.length})
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {message.photos.map((photo) => (
                      <PhotoImage key={photo._id} path={photo.path} />
                    ))}
                  </Box>
                </Box>
              </>
            )}

            {/* Admin reply */}
            {message.adminReplyText && (
              <>
                <Divider />
                <Box sx={{ bgcolor: 'success.light', p: 2, borderRadius: 1, opacity: 0.9 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" color="success.dark">
                      Admin Reply
                    </Typography>
                    {message.repliedAt && (
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(message.repliedAt)}
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>{message.adminReplyText}</Typography>
                </Box>
              </>
            )}

            {/* Reply form */}
            {message.status !== ESupportMessageStatus.Replied && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Reply
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Type your reply..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    disabled={isReplying}
                  />
                  <Box display="flex" justifyContent="flex-end" mt={1}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={isReplying ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                      onClick={handleReply}
                      disabled={!replyText.trim() || isReplying}
                    >
                      Send Reply
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
